// Imported from the same 'react-phone-number-input/input' subpath as
// PhoneNoField, not the bare 'react-phone-number-input' entry — the package
// exports several metadata-bundle variants per subpath, and importing two
// different ones bundled both, showing up as a "duplicate JavaScript module"
// in PageSpeed. The /input entry re-exports these same validators.
import { Country, isPossiblePhoneNumber } from 'react-phone-number-input/input';

/**
 * Validates a password string against multiple criteria, ensuring it contains:
 * - At least one lowercase letter
 * - At least one uppercase letter
 * - At least one number
 * - At least one special character from the set (!@#$%&?)
 *
 * @param {string} value - The password string to validate.
 * @returns {string | undefined} - Returns an error message if the password doesn't meet all criteria, or `undefined` if it does.
 */
export const passwordValidator = (value: string, label = 'Password') => {
  // Map of regex patterns to their corresponding error messages
  const requirements = new Map([
    [/[a-z]/, 'lowercase letter'],
    [/[A-Z]/, 'uppercase letter'],
    [/\d/, 'number'],
    [/[!@#$%&?]/, 'special character (!@#$%&?)'],
  ]);

  // Regex to disallow any characters not in the allowed sets
  const disallowedCharacters = /[^a-zA-Z\d!@#$%&?]/;

  // Check if the password contains any disallowed characters
  if (disallowedCharacters.test(value)) {
    return `${label} contains invalid characters. Only letters, numbers, and special characters (!@#$%&?) are allowed.`;
  }

  // Iterate through each character in the string
  for (const char of value) {
    // Test each regex in the requirements map against the current character
    for (const [regex] of requirements) {
      if (regex.test(char)) {
        // Remove the matched regex from the map
        requirements.delete(regex);
        // Stop checking further regex patterns for this character
        break;
      }
    }

    // Exit early if all conditions are met
    if (requirements.size === 0) {
      return undefined;
    }
  }

  // Construct the error message if any conditions are missing
  const missingRequirements = Array.from(requirements.values());
  const lastError =
    missingRequirements.length > 1
      ? ', and one ' + missingRequirements.pop()
      : '';

  return (
    `${label} must contain at least one ` +
    missingRequirements.join(', one ') +
    lastError
  );
};

/**
 * Validates a mobile number string against a given country code, ensuring it is a possible phone number for that country.
 *
 * @param {string} value - The mobile number string to validate.
 * @param {Country} countryCode - The country code to validate against.
 * @returns {string | undefined} - Returns an error message if the phone number is invalid for the given country code, or `undefined` if it is valid.
 */
export const MobileNoValidator = (value: string, countryCode: string) =>
  isPossiblePhoneNumber(value, countryCode as Country)
    ? undefined
    : 'Invalid phone number';
